import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Lock, CheckCircle2, ArrowLeft } from 'lucide-react';
import { useRouteLocale } from '@/hooks/useRouteLocale';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoginHeader } from '@/components/login';
import { PUBLIC_ROUTES } from '@/routes';
import { confirmPasswordReset } from '@/services/api/auth';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const locale = useRouteLocale();
  const currentLocale = locale || 'ro';

  usePageMeta({
    title: t('auth.passwordResetConfirm.metaTitle'),
    description: t('auth.passwordResetConfirm.metaTitle'),
    locale: locale,
    path: PUBLIC_ROUTES.resetPassword(locale),
    noindex: true,
  });

  const tokenParam = searchParams.get('token') || '';
  const emailParam = searchParams.get('email') || '';

  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);

  const resetSchema = z
    .object({
      user_email: z.string().min(1, t('validation.emailRequired')).email(t('validation.emailInvalid')),
      user_password: z.string().min(8, t('validation.passwordMinLength')),
      user_password_confirmation: z.string().min(8, t('validation.passwordMinLength')),
    })
    .refine((data) => data.user_password === data.user_password_confirmation, {
      message: t('validation.passwordsDontMatch'),
      path: ['user_password_confirmation'],
    });

  type ResetFormData = z.infer<typeof resetSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      user_email: emailParam,
      user_password: '',
      user_password_confirmation: '',
    },
  });

  const handleGoToLogin = () => {
    navigate(PUBLIC_ROUTES.login(currentLocale));
  };

  const onSubmit = async (data: ResetFormData) => {
    if (!tokenParam) {
      toast.error(t('auth.passwordResetConfirm.invalidToken'));
      return;
    }

    try {
      setLoading(true);
      await confirmPasswordReset({
        token: tokenParam,
        user_email: data.user_email,
        user_password: data.user_password,
      });
      setCompleted(true);
      toast.success(t('auth.passwordResetConfirm.success'));
    } catch {
      toast.error(t('auth.passwordResetConfirm.invalidToken'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LoginHeader currentLocale={currentLocale} action={{ label: t('auth.signIn'), onClick: handleGoToLogin }} />

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-card border rounded-lg p-8 shadow-sm">
            {completed ? (
              <div className="text-center space-y-6" data-testid="password-reset-completed">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30">
                  <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-foreground mb-2">
                    {t('auth.passwordResetConfirm.completedTitle')}
                  </h1>
                  <p className="text-muted-foreground">
                    {t('auth.passwordResetConfirm.completedSubtitle')}
                  </p>
                </div>
                <Button onClick={handleGoToLogin} className="w-full" data-testid="reset-password-login-button">
                  {t('auth.goToLogin')}
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pink-100 dark:bg-pink-900/30 mb-4">
                    <Lock className="h-8 w-8 text-pink-600 dark:text-pink-400" />
                  </div>
                  <h1 className="text-2xl font-semibold text-foreground mb-2">
                    {t('auth.passwordResetConfirm.title')}
                  </h1>
                  <p className="text-muted-foreground">
                    {t('auth.passwordResetConfirm.subtitle')}
                  </p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <Label htmlFor="user_email">{t('auth.email')}</Label>
                    <Input
                      id="user_email"
                      type="email"
                      {...register('user_email')}
                      data-testid="reset-password-email-input"
                      className="mt-1"
                    />
                    {errors.user_email && (
                      <p className="text-sm text-destructive mt-1">{errors.user_email.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="user_password">{t('auth.passwordResetConfirm.newPassword')}</Label>
                    <Input
                      id="user_password"
                      type="password"
                      {...register('user_password')}
                      data-testid="reset-password-new-input"
                      className="mt-1"
                    />
                    {errors.user_password && (
                      <p className="text-sm text-destructive mt-1">{errors.user_password.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="user_password_confirmation">{t('auth.confirmPassword')}</Label>
                    <Input
                      id="user_password_confirmation"
                      type="password"
                      {...register('user_password_confirmation')}
                      data-testid="reset-password-confirm-input"
                      className="mt-1"
                    />
                    {errors.user_password_confirmation && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.user_password_confirmation.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading}
                    data-testid="reset-password-submit-button"
                  >
                    {loading ? t('common.loading') : t('auth.passwordResetConfirm.submit')}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <Button variant="ghost" onClick={handleGoToLogin} className="w-full">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t('auth.backToLogin')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
